import { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import TextField from "components/common/TextField";
import { Sparkles } from "lucide-react";
import useIsMobile from "components/hooks/useIsMobile";
import { CurrentUserContext } from "components/context/RequireAuth";
import { useError } from "components/context/HttpErrorProvider";
import {
  useListAssignmentCandidates,
  useSendAssignmentOpportunities,
} from "services/signup";
import {
  useAddRematchAdvisors,
  useRemoveAdvisorFromEntity,
  useShareRematchOpportunities,
  useUpdateRematchSession,
} from "services/admin";
import { RematchSessionStatus } from "protogen/advisors_service_pb";
import {
  AssignmentCandidate,
  SignupSubmission,
} from "protogen/signup_service_pb";
import { RematchSession } from "protogen/advisors_service_pb";
import SearchBar from "components/common/SearchBar";
import GenerateMatches, {
  MatchReasonMap,
} from "components/matchmaking/GenerateMatches";
import AdvisorRow from "./AdvisorRow";
import Loading from "components/common/Loading";
import { generateClientEmail, isNewAdvisor } from "./utils";
import EmailComposeDialog from "components/email/EmailComposeDialog";
import { EmailMessage } from "protogen/conversation_pb";

const AdvisorListWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        border: "1px solid #D4D4D4",
      }}
    >
      {children}
    </Box>
  );
};

type Props = {
  lead?: SignupSubmission;
  familyRef?: string;
  rematchSessionRef?: string;
  rematchSession?: RematchSession;
  onStarted: () => void;
  onRematchSessionUpdate?: (session: RematchSession) => void;
};

export default ({
  lead,
  familyRef,
  rematchSessionRef,
  rematchSession,
  onStarted,
  onRematchSessionUpdate,
}: Props) => {
  const isMobile = useIsMobile();
  const currentUser = useContext(CurrentUserContext);
  const { addError } = useError();
  const [advisors, setAdvisors] = useState<AssignmentCandidate[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyNewAdvisors, setShowOnlyNewAdvisors] = useState(false);
  const [generateMatchesOpen, setGenerateMatchesOpen] = useState(false);
  const [generateMatchesRunning, setGenerateMatchesRunning] = useState(false);
  const [matchReasonMap, setMatchReasonMap] = useState<MatchReasonMap>({});
  const [emailComposeOpen, setEmailComposeOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState<EmailMessage | undefined>(
    undefined,
  );
  const {
    request: sendAssignmentOpportunities,
    loading: sendingOpportunities,
  } = useSendAssignmentOpportunities();
  const { request: addRematchAdvisors, loading: addingRematchAdvisors } =
    useAddRematchAdvisors((response) => {
      if (response.session && onRematchSessionUpdate) {
        onRematchSessionUpdate(response.session);
      }
      onStarted();
    });
  const { request: removeAdvisorFromEntity } = useRemoveAdvisorFromEntity(
    () => {
      onStarted();
    },
  );
  const { request: updateRematchSession } = useUpdateRematchSession(
    (response) => {
      if (response.session && onRematchSessionUpdate) {
        onRematchSessionUpdate(response.session);
      }
    },
  );
  const { request: shareRematchOpportunities, loading: sharingOpportunities } =
    useShareRematchOpportunities((response) => {
      if (response.session && onRematchSessionUpdate) {
        onRematchSessionUpdate(response.session);
      }
      onStarted();
    });
  const {
    request: fetchAssignmentCandidates,
    data: assignmentCandidatesData,
    loading: assignmentCandidatesLoading,
  } = useListAssignmentCandidates();
  useEffect(() => {
    fetchAssignmentCandidates({
      leadRef: lead?.ref || "",
      familyRef: familyRef || "",
      sortByDistance: true,
      includeMMForm: true,
    });
  }, []);

  useEffect(() => {
    setAdvisors([]);
  }, [lead]);

  // Get existing assignments and their advisor refs
  const existingAssignments =
    lead?.assignments || rematchSession?.assignments || [];

  const queuedAssignments = existingAssignments.filter(
    (a) => a.status === "queued",
  );
  const acceptedAssignments = existingAssignments.filter(
    (a) => a.status === "accepted",
  );

  // The blast is async: after sending, advisors stay "queued" until the worker flips them to
  // "waiting", so we track who we've already sent to locally to keep the button from flashing back
  // to an enabled state before the refresh lands.
  const [sentAdvisorRefs, setSentAdvisorRefs] = useState<Set<string>>(
    new Set(),
  );
  const DEFAULT_TIMEOUT_HOURS = 4;
  const [timeoutHours, setTimeoutHours] = useState<number>(
    DEFAULT_TIMEOUT_HOURS,
  );
  const queuedRefsKey = queuedAssignments.map((a) => a.advisor?.ref).join(",");
  useEffect(() => {
    // Forget advisors that are no longer queued so re-adding one later re-enables the button.
    setSentAdvisorRefs((prev) => {
      const queuedRefs = new Set(
        queuedAssignments.map((a) => a.advisor?.ref || ""),
      );
      const next = new Set(
        Array.from(prev).filter((ref) => queuedRefs.has(ref)),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [queuedRefsKey]);
  const allQueuedSent =
    queuedAssignments.length > 0 &&
    queuedAssignments.every((a) => sentAdvisorRefs.has(a.advisor?.ref || ""));

  // Get the full list of advisors for looking up existing assignments
  const allCandidates = assignmentCandidatesData?.candidates || [];

  const filteredCandidates =
    assignmentCandidatesData?.candidates.filter((candidate) => {
      const mmForm = candidate.mmForm;
      const matchesSearch =
        candidate.advisor?.firstName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        candidate.advisor?.lastName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        candidate.advisor?.displayName
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        candidate.advisor?.city
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        candidate.advisor?.state
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        candidate.superPowers
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        candidate.advisor?.blurbText
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (mmForm && mmForm.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesNewAdvisorFilter =
        !showOnlyNewAdvisors || isNewAdvisor(candidate);

      return matchesSearch && matchesNewAdvisorFilter;
    }) || [];

  const onSend = async () => {
    if (advisors.length === 0) return;

    if (rematchSessionRef) {
      // For rematch sessions: only add advisors (don't share yet). Advisors get 4 hours to respond
      // once the opportunity is actually blasted.
      await addRematchAdvisors({
        rematchSessionRef,
        advisorRefs: advisors.map((a) => a.advisor?.ref || ""),
        timeoutMinutes: 240,
      });
      setAdvisors([]);
      onStarted();
    } else if (lead) {
      // For signup submissions: use existing flow
      const selected = advisors;
      setAdvisors([]);
      try {
        await sendAssignmentOpportunities({
          entityType: "signup_submission",
          entityRef: lead.ref,
          advisorRefs: selected.map((a) => a.advisor?.ref || ""),
          appendToExisting: lead.assignments.length > 0,
        });
      } catch (e) {
        setAdvisors(selected);
        addError(
          e instanceof Error ? e.message : "Failed to share opportunities",
        );
      }
      onStarted();
    }
  };

  const handleSelect = (advisor: AssignmentCandidate) => {
    if (!advisors.some((a) => a.advisor?.ref === advisor.advisor?.ref)) {
      setAdvisors([...advisors, advisor]);
    }
  };

  const handleRemove = (advisor: AssignmentCandidate) => {
    setAdvisors(
      advisors.filter((a) => a.advisor?.ref !== advisor.advisor?.ref),
    );
  };

  const moveUp = (index: number) => {
    if (index > 0) {
      const newAdvisors = [...advisors];
      const temp = newAdvisors[index];
      newAdvisors[index] = newAdvisors[index - 1];
      newAdvisors[index - 1] = temp;
      setAdvisors(newAdvisors);
    }
  };

  const moveDown = (index: number) => {
    if (index < advisors.length - 1) {
      const newAdvisors = [...advisors];
      const temp = newAdvisors[index];
      newAdvisors[index] = newAdvisors[index + 1];
      newAdvisors[index + 1] = temp;
      setAdvisors(newAdvisors);
    }
  };

  const handleRemoveAdvisor = async (advisorRef: string) => {
    const entityType = rematchSessionRef
      ? "rematch_session"
      : "signup_submission";
    const entityRef = rematchSessionRef || lead?.ref || "";

    await removeAdvisorFromEntity({
      entityType,
      entityRef,
      advisorRef,
    });
  };

  const handleSendOpportunities = async () => {
    if (!rematchSessionRef) return;
    const hours = timeoutHours > 0 ? timeoutHours : DEFAULT_TIMEOUT_HOURS;
    setSentAdvisorRefs((prev) => {
      const next = new Set(prev);
      queuedAssignments.forEach((a) => next.add(a.advisor?.ref || ""));
      return next;
    });
    await shareRematchOpportunities({
      rematchSessionRef,
      timeoutMinutes: Math.round(hours * 60),
    });
  };

  const handleGenerateEmail = () => {
    if (!rematchSession) return;
    // Only advisors who said yes get their profile shared with the client.
    const { text, html } = generateClientEmail(
      rematchSession,
      acceptedAssignments,
      currentUser.firstName,
    );
    const clientEmail =
      rematchSession.family?.familyMembers?.[0]?.primaryEmail || "";

    const draft = new EmailMessage({
      subject: `Re-matching your Faye Advisor`,
      text,
      html,
      toAddresses: clientEmail ? [clientEmail] : [],
      isDraft: true,
      emailType: "administrative",
    });

    setEmailDraft(draft);
    setEmailComposeOpen(true);
  };

  const needsMatchmaking =
    rematchSessionRef ||
    familyRef ||
    (lead &&
      (lead.pipelineStatus === "awaiting-match" ||
        lead.pipelineStatus === "signup-complete"));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "42px" }}>
      {/* Selected Advisors Table */}
      {(lead || rematchSessionRef) && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box
              sx={{
                display: "flex",
                gap: "8px",
                padding: "12px 0",
              }}
            >
              <Typography variant="h3">
                {isMobile ? "Selected" : "Selected Advisors"}
              </Typography>
              <Typography variant="bodyHeavy" color="text.tertiary">
                {existingAssignments.length + advisors.length}
              </Typography>
            </Box>

            {needsMatchmaking && (
              <Button
                variant="contained"
                onClick={onSend}
                disabled={
                  advisors.length === 0 ||
                  sendingOpportunities ||
                  addingRematchAdvisors
                }
                sx={{ alignSelf: "flex-end" }}
              >
                {rematchSessionRef
                  ? "Add advisors"
                  : isMobile
                    ? "Share"
                    : "Share opportunities"}
              </Button>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
            }}
          >
            {existingAssignments.length === 0 && advisors.length === 0 ? (
              <Box sx={{ width: "100%", textAlign: "center", py: 4 }}>
                <Typography variant="body" sx={{ color: "text.secondary" }}>
                  No advisors selected
                </Typography>
              </Box>
            ) : (
              <>
                {/* Existing Assignments */}
                <AdvisorListWrapper>
                  {existingAssignments.map((assignment, index) => {
                    // For rematch sessions, we might not have candidates, but we have the assignment data
                    const candidate = allCandidates.find(
                      (c) => c.advisor?.ref === assignment.advisor?.ref,
                    );

                    // If no candidate found but we have assignment data, create a minimal candidate
                    const displayCandidate =
                      candidate ||
                      ({
                        advisor: assignment.advisor,
                        distanceMiles: 0,
                        superPowers: "",
                        mmForm: "",
                      } as AssignmentCandidate);

                    return (
                      <AdvisorRow
                        key={assignment.advisor?.ref}
                        candidate={displayCandidate}
                        assignment={assignment}
                        actionType="existing"
                        isLast={
                          advisors.length === 0 &&
                          index === existingAssignments.length - 1
                        }
                        lead={lead}
                        rematchSession={rematchSession}
                        generateMatchesRunning={generateMatchesRunning}
                        matchReason={matchReasonMap[assignment.advisor?.ref]}
                        refresh={onStarted}
                        handleRemoveAdvisor={handleRemoveAdvisor}
                      />
                    );
                  })}
                  {/* Newly Selected Advisors */}
                  {advisors.map((candidate, index) => (
                    <AdvisorRow
                      key={candidate.advisor?.ref}
                      candidate={candidate}
                      actionType="new"
                      advisors={advisors}
                      index={index}
                      moveUp={moveUp}
                      moveDown={moveDown}
                      handleRemove={handleRemove}
                      isLast={index === advisors.length - 1}
                      lead={lead}
                      generateMatchesRunning={generateMatchesRunning}
                      matchReason={matchReasonMap[candidate.advisor?.ref]}
                    />
                  ))}
                </AdvisorListWrapper>
              </>
            )}
          </Box>

          {rematchSession &&
            (rematchSession.status === "active_draft" ||
              rematchSession.status === "active") && (
              <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                {/* Blast the opportunity to everyone still queued. */}
                {queuedAssignments.length > 0 && (
                  <Box
                    sx={{ display: "flex", gap: "8px", alignItems: "center" }}
                  >
                    <TextField
                      type="number"
                      size="small"
                      label="Response window (hrs)"
                      value={timeoutHours}
                      onChange={(e) => setTimeoutHours(Number(e.target.value))}
                      disabled={sharingOpportunities || allQueuedSent}
                      inputProps={{ min: 1 }}
                      sx={{ width: "160px" }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleSendOpportunities}
                      disabled={sharingOpportunities || allQueuedSent}
                      sx={{ alignSelf: "flex-start" }}
                    >
                      {sharingOpportunities
                        ? "Sending..."
                        : allQueuedSent
                          ? "Opportunity sent"
                          : "Send opportunity to Advisors"}
                    </Button>
                  </Box>
                )}
                {/* Only enabled once at least one advisor has said yes. */}
                {acceptedAssignments.length > 0 && (
                  <Button
                    variant="outlined"
                    onClick={handleGenerateEmail}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Generate email to client
                  </Button>
                )}
              </Box>
            )}
        </Box>
      )}
      {/* Available Advisors Table */}
      {needsMatchmaking && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
              }}
            >
              <Box sx={{ display: "flex", gap: "8px" }}>
                <Typography variant="h3">Advisors </Typography>
                <Typography variant="bodyHeavy" color="text.tertiary">
                  {
                    filteredCandidates.filter(
                      (candidate) =>
                        !existingAssignments.some(
                          (a) => a.advisor?.ref === candidate.advisor?.ref,
                        ) &&
                        !advisors.some(
                          (a) => a.advisor?.ref === candidate.advisor?.ref,
                        ),
                    ).length
                  }
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showOnlyNewAdvisors}
                    onChange={(e) => setShowOnlyNewAdvisors(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "14px", color: "text.secondary" }}
                  >
                    Only new advisors
                  </Typography>
                }
                sx={{ margin: 0 }}
              />
            </Box>
            <Box display="flex" gap={2} alignItems="center">
              {(lead || rematchSession) && (
                <Button
                  variant="outlined"
                  startIcon={<Sparkles />}
                  onClick={() => setGenerateMatchesOpen(!generateMatchesOpen)}
                  sx={{
                    height: "40px", // Match SearchBar height
                    whiteSpace: "nowrap", // Prevent text wrapping
                  }}
                >
                  Generate matches
                </Button>
              )}
              <SearchBar
                initialQuery={searchTerm}
                onQuery={setSearchTerm}
                expand={true}
                autofocus={true}
                onKeyPress={true}
              />
            </Box>
          </Box>
          {(lead || rematchSession) && (
            <GenerateMatches
              lead={lead}
              rematchSession={rematchSession}
              open={generateMatchesOpen}
              setOpen={setGenerateMatchesOpen}
              setRunning={setGenerateMatchesRunning}
              setMatches={setMatchReasonMap}
              runEagerly={!rematchSession}
            />
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
            }}
          >
            {/* Available Advisors */}
            <AdvisorListWrapper>
              {filteredCandidates
                .filter(
                  (candidate) =>
                    !existingAssignments.some(
                      (c) => c.advisor?.ref === candidate.advisor?.ref,
                    ) &&
                    !advisors.some(
                      (c) => c.advisor?.ref === candidate.advisor?.ref,
                    ),
                )
                .map((candidate, index) => (
                  <AdvisorRow
                    key={candidate.advisor?.ref}
                    candidate={candidate}
                    actionType="available"
                    handleSelect={handleSelect}
                    isLast={index === filteredCandidates.length - 1}
                    lead={lead}
                    rematchSessionRef={rematchSessionRef}
                    generateMatchesRunning={generateMatchesRunning}
                    matchReason={matchReasonMap[candidate.advisor?.ref || ""]}
                  />
                ))}
            </AdvisorListWrapper>

            {/* Empty State */}
            {filteredCandidates.filter(
              (candidate) =>
                !existingAssignments.some(
                  (a) => a.advisor?.ref === candidate.advisor?.ref,
                ) &&
                !advisors.some(
                  (c) => c.advisor?.ref === candidate.advisor?.ref,
                ),
            ).length === 0 &&
              !assignmentCandidatesLoading && (
                <Box sx={{ width: "100%", textAlign: "center", py: 4 }}>
                  <Typography variant="body" sx={{ color: "text.secondary" }}>
                    No advisors match your filters
                  </Typography>
                </Box>
              )}
            {assignmentCandidatesLoading && <Loading />}
          </Box>
        </Box>
      )}

      {/* Email Compose Dialog */}
      <EmailComposeDialog
        closed={!emailComposeOpen}
        onClose={() => setEmailComposeOpen(false)}
        onSent={async () => {
          if (rematchSession) {
            await updateRematchSession({
              rematchSessionRef: rematchSession.ref,
              status: RematchSessionStatus.REMATCH_CLIENT_EMAIL_SENT,
            });
          }
          setEmailComposeOpen(false);
        }}
        onRemove={() => {}}
        initialDraft={emailDraft}
      />
    </Box>
  );
};
